import type { ContextGeoSubject } from '@/shared/types/geography.ts';
import { trackEvent } from '@/entities/sogu';
import { useScreenSize } from '@experience/shared';
import { useExcursionFiltersStore, type TExcursionFiltersState } from '@/entities/filter/store/excursion';
import { excursionMapFilterDataToSogu } from '@/entities/filter/store/utils/excursion/ExcursionMapFilterDataToSogu.ts';
import { removeNullableProperties } from '@/shared/lib/removeNullableProperties.ts';
import { useFiltersDateRange } from '@/features/filter/dates/composables/useFiltersDateRange.ts';

export type ExcursionSoguFilterTypes =
  | 'price'
  | 'movement_type'
  | 'all'
  | 'duration'
  | 'date'
  | 'date_mobile'
  | 'type'
  | 'activity_type';

export function useExcursionFilterBlockSogu() {
  const { isMobile } = useScreenSize();
  const filterStore = useExcursionFiltersStore();
  const { fastDateRangeRef, lastAppliedFastDateRangeRef } = useFiltersDateRange();

  const excursionSoguFilterOpenEvent = (
    soguFields: ContextGeoSubject,
    filterType: ExcursionSoguFilterTypes,
    isForced: boolean = false,
  ) => {
    if (filterType === 'date' && isMobile.value) {
      filterType = 'date_mobile';
    }

    return trackEvent('listing:filter.open', buildSoguEventParams(soguFields, filterType, isForced));
  };

  const excursionSoguFilterApplyEvent = (
    soguFields: ContextGeoSubject,
    filterType: ExcursionSoguFilterTypes,
    isForced: boolean = false,
    customFilterData?: Partial<TExcursionFiltersState>,
  ) => {
    if (filterType === 'date' && isMobile.value) {
      filterType = 'date_mobile';
    }

    const dataToMap = customFilterData || filterStore.searchState;

    const { price_start, price_end, ...mappedParams } = removeNullableProperties(
      excursionMapFilterDataToSogu(dataToMap),
    );

    const params: Record<string, unknown> = {
      ...mappedParams,
    };

    if (price_start) {
      params.start_price = price_start;
    }

    if (price_end) {
      params.end_price = price_end;
    }

    if (fastDateRangeRef.value) {
      params.fast_date_range = fastDateRangeRef.value;
      lastAppliedFastDateRangeRef.value = fastDateRangeRef.value;
    } else {
      lastAppliedFastDateRangeRef.value = null;
    }

    return trackEvent('listing:filter.apply', buildSoguEventParams(soguFields, filterType, isForced, params));
  };

  const excursionSoguFilterResetEvent = (
    soguFields: ContextGeoSubject,
    filterType: ExcursionSoguFilterTypes,
    isForced: boolean = false,
  ) => {
    const params: Record<string, unknown> = removeNullableProperties(
      excursionMapFilterDataToSogu(filterStore.searchState),
    );

    if (lastAppliedFastDateRangeRef.value) {
      params.fast_date_range = lastAppliedFastDateRangeRef.value;
    }

    return trackEvent('listing:filter.reset', buildSoguEventParams(soguFields, filterType, isForced, params));
  };

  const buildSoguEventParams = (
    soguFields: ContextGeoSubject,
    filterType: ExcursionSoguFilterTypes,
    isForced: boolean = false,
    additionalParams: Record<string, unknown> = {},
  ) => {
    return {
      ...soguFields.soguStamp,
      filter_type: filterType,
      filter_window_type: isForced ? 'auto_listing_open_popup' : 'manual_listing_open_popup',
      ...additionalParams,
    };
  };

  return {
    excursionSoguFilterOpenEvent,
    excursionSoguFilterApplyEvent,
    excursionSoguFilterResetEvent,
  };
}
